import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TableContainer } from "@mui/material";
import {
	CustomDialog,
	CustomTable,
	CustomTableBody,
	CustomTableHead,
	HorizontalDivider,
	icon,
} from "../../../../common/components";
import { setBulkVersioningInProgress, setHasReceivedModifications } from "../../slice/document-center-slice";
import { translate } from "../../../../common/providers";
import styles from "./BulkVersioningDialog.module.css";
import { ProjectDocumentService } from "../../../../api";
import { useApi, useTrackEvent } from "../../../../common/hooks";
import { BulkTableBodyRow, BulkTableHeaderRow, BulkTablePagination, HelperBox } from "./components";
import { isNonEmptyArray } from "../../../../common/utils";
import { VERSIONING_STATUS } from "../../constants/constants";

const BulkVersioningDialog = () => {
	const { call: getAllDocVersionCandidates } = useApi(ProjectDocumentService.getAllDocVersionCandidates);
	const { call: getAllSimilarDocVersionCandidatesForSelection } = useApi(
		ProjectDocumentService.getAllSimilarDocVersionCandidatesForSelection
	);
	const { call: getAllSimilarDocVersionCandidates } = useApi(
		ProjectDocumentService.getAllSimilarDocVersionCandidates
	);
	const { call: updateBulkVersioningCandidatesStatus } = useApi(
		ProjectDocumentService.updateBulkVersioningCandidatesStatus
	);
	const projectId = useSelector(({ context }) => context.project.id);
	const bulkVersioningInProgress = useSelector(({ documentCenter }) => documentCenter.bulkVersioningInProgress);
	const [openHelperBox, setOpenHelperBox] = useState(true);
	const [versioningCandidates, setVersioningCandidates] = useState({});
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(50);
	const [selectedIds, setSelectedIds] = useState([]);
	const [updatePayload, setUpdatePayload] = useState([]);
	const [disabledIds, setDisabledIds] = useState([]);
	const [sort, setSort] = useState("");
	const [direction, setDirection] = useState("");
	const [loadingSelected, setLoadingSelection] = useState(false);
	const [loadingStatus, setLoadingStatus] = useState(false);
	const [warningMessage, setWarningMessage] = useState("");
	const helperBoxRef = useRef(null);
	const trackEvent = useTrackEvent();
	const hasUpdates = useMemo(
		() => isNonEmptyArray(updatePayload?.filter((up) => up.status !== VERSIONING_STATUS.CREATED || up.toInverse)),
		[updatePayload]
	);
	const dispatch = useDispatch();
	useEffect(() => {
		if (bulkVersioningInProgress) {
			getAllDocVersionCandidates(
				{ projectId },
				{ page, limit, sort: sort || undefined, direction: direction || undefined }
			)
				.then(setVersioningCandidates)
				.catch(console.error);
		}
	}, [getAllDocVersionCandidates, projectId, page, sort, direction, limit, bulkVersioningInProgress]);
	const handleClose = () => {
		setSelectedIds([]);
		setDisabledIds([]);
		setUpdatePayload([]);
		setPage(0);
		setLimit(50);
		setVersioningCandidates({});
		setSort("");
		setDirection("");
		dispatch(setBulkVersioningInProgress(false));
		setWarningMessage("");
	};
	const handleCloseHelperBox = () => {
		setOpenHelperBox(false);
	};
	const handleChangePage = (_, p) => {
		if (versioningCandidates.totalElements > 0) {
			dispatch(setPage(p));
		}
	};
	const handleChangeLimitPerPage = (e) => {
		if (versioningCandidates.totalElements > 0) {
			dispatch(setLimit(e.target?.value || 50));
			dispatch(setPage(0));
		}
	};
	const handleSelect = (isSelected, id) => {
		const newSelectedIds = isSelected
			? selectedIds.filter((selectedId) => selectedId !== id)
			: [...selectedIds, id];
		if (isNonEmptyArray(newSelectedIds)) {
			setLoadingSelection(true);
			getAllSimilarDocVersionCandidatesForSelection({ projectId }, { candidateIds: newSelectedIds })
				.then((data) => {
					setSelectedIds(newSelectedIds);
					setDisabledIds(data);
				})
				.catch(console.error)
				.finally(() => {
					setLoadingSelection(false);
				});
		} else {
			setSelectedIds([]);
			setDisabledIds([]);
		}
	};
	const handleSelectAll = () => {
		const allCurrentPageIds = versioningCandidates?.contents?.map((c) => c.candidateId) || [];
		const allSelected = allCurrentPageIds.every((id) => selectedIds.includes(id)) && allCurrentPageIds.length > 0;

		if (allSelected) {
			setSelectedIds([]);
			setDisabledIds([]);
		} else {
			const newSelectedIds = [...new Set([...selectedIds, ...allCurrentPageIds])];
			if (isNonEmptyArray(newSelectedIds)) {
				setLoadingSelection(true);
				getAllSimilarDocVersionCandidatesForSelection({ projectId }, { candidateIds: newSelectedIds })
					.then((data) => {
						setSelectedIds(newSelectedIds);
						setDisabledIds(data);
					})
					.catch(console.error)
					.finally(() => {
						setLoadingSelection(false);
					});
			}
		}
	};
	const handleInverse = (id) => {
		const index = updatePayload.findIndex((p) => p.id === id);
		if (index === -1) {
			setUpdatePayload((prev) => [...prev, { id, toInverse: true, status: VERSIONING_STATUS.CREATED }]);
		} else {
			setUpdatePayload((prev) => {
				const newPayload = [...prev];
				newPayload[index] = { ...newPayload[index], toInverse: !newPayload[index].toInverse };
				return newPayload;
			});
		}
	};
	const handleChangeStatus = (id, status) => {
		const index = updatePayload.findIndex((p) => p.id === id);
		if (index === -1) {
			setUpdatePayload((prev) => [...prev, { id, toInverse: false, status }]);
		} else {
			setUpdatePayload((prev) => {
				const newPayload = [...prev];
				newPayload[index] = { ...newPayload[index], status };
				return newPayload;
			});
		}
	};
	const handleInitChangeStatus = (id, status) => {
		if (status === VERSIONING_STATUS.VALIDATED) {
			setLoadingStatus(true);
			getAllSimilarDocVersionCandidates({ projectId, candidateId: id })
				.then((data) => {
					data.filter((d) => d !== id).forEach((candidateId) => {
						handleChangeStatus(candidateId, VERSIONING_STATUS.DISCARDED);
					});
					handleChangeStatus(id, VERSIONING_STATUS.VALIDATED);
				})
				.catch(console.error)
				.finally(() => {
					setLoadingStatus(false);
				});
		} else {
			handleChangeStatus(id, status);
		}
	};
	const handleConfirmBulk = () => {
		selectedIds.forEach((id) => {
			handleInitChangeStatus(id, VERSIONING_STATUS.VALIDATED);
		});
	};
	const handleRejectBulk = () => {
		selectedIds.forEach((id) => {
			handleInitChangeStatus(id, VERSIONING_STATUS.DISCARDED);
		});
	};
	const handleToValidateBulk = () => {
		selectedIds.forEach((id) => {
			handleInitChangeStatus(id, VERSIONING_STATUS.CREATED);
		});
	};
	const handleInverseBulk = () => {
		selectedIds.forEach((id) => {
			handleInverse(id);
		});
	};

	const handleUpdate = () => {
		updateBulkVersioningCandidatesStatus({ projectId }, { payload: updatePayload })
			.then(() => {
				if (updatePayload?.some((up) => up?.status !== VERSIONING_STATUS.VALIDATED)) {
					dispatch(setHasReceivedModifications(true));
				}
				trackEvent("Document_Bulk_V2_Validates_Panel");
				handleClose();
			})
			.catch(console.error);
	};
	const initUpdate = () => {
		if (hasUpdates) {
			setWarningMessage("document-center.table.bulk-versioning-dialog.validate");
		} else {
			handleClose();
		}
	};
	const initClose = () => {
		if (hasUpdates) {
			setWarningMessage("document-center.table.bulk-versioning-dialog.cancel");
		} else {
			handleClose();
		}
	};
	const handleConfirm = () => {
		if (warningMessage === "document-center.table.bulk-versioning-dialog.validate") {
			handleUpdate();
		} else {
			handleClose();
		}
	};
	const handleSort = (property) => {
		const isAsc = sort === property && direction === "ASC";
		setDirection(isAsc ? "DESC" : "ASC");
		setSort(property);
	};

	return (
		<>
			<CustomDialog
				disableBackdropClick
				className={styles.content}
				classNameDialog={styles.dialog}
				closeLabel={translate("common:btn.close")}
				open={bulkVersioningInProgress}
				submitDisabled={!hasUpdates}
				submitLabel={translate("common:btn.validate")}
				title={translate("document-center.table.bulk-versioning-dialog.title")}
				onClose={initClose}
				onSubmit={initUpdate}
			>
				{openHelperBox && (
					<HelperBox
						ref={helperBoxRef}
						subTitle={translate("document-center.table.bulk-versioning-dialog.helper-box.sub-title")}
						title={translate("document-center.table.bulk-versioning-dialog.helper-box.title")}
						onClose={handleCloseHelperBox}
					/>
				)}
				<TableContainer
					style={{
						height: `calc(100vh - ${
							220 + (openHelperBox ? helperBoxRef.current?.offsetHeight || 0 : 0)
						}px)`,
					}}
				>
					<CustomTable>
						<CustomTableHead>
							<BulkTableHeaderRow
								activeSort={sort}
								direction={direction}
								isChecked={
									versioningCandidates?.contents?.length > 0 &&
									versioningCandidates.contents.every((c) => selectedIds.includes(c.candidateId))
								}
								isIndeterminate={
									selectedIds.length > 0 &&
									!(
										versioningCandidates?.contents?.length > 0 &&
										versioningCandidates.contents.every((c) => selectedIds.includes(c.candidateId))
									)
								}
								selectedCount={selectedIds.length}
								onConfirm={handleConfirmBulk}
								onInverse={handleInverseBulk}
								onReject={handleRejectBulk}
								onSelectAll={handleSelectAll}
								onSort={handleSort}
								onValidate={handleToValidateBulk}
							/>
						</CustomTableHead>
						<CustomTableBody>
							{isNonEmptyArray(versioningCandidates.contents) &&
								versioningCandidates.contents.map((candidate) => {
									const updates = updatePayload.find((p) => p.id === candidate.candidateId);
									return (
										<BulkTableBodyRow
											key={candidate.candidateId}
											disabled={
												(disabledIds.includes(candidate.candidateId) &&
													!selectedIds.includes(candidate.candidateId)) ||
												loadingSelected
											}
											disableStatusSelection={loadingStatus}
											isChecked={selectedIds.includes(candidate.candidateId)}
											isInversed={updates?.toInverse}
											row={candidate}
											status={updates?.status}
											onChangeStatus={handleInitChangeStatus}
											onInverse={handleInverse}
											onSelect={handleSelect}
										/>
									);
								})}
						</CustomTableBody>
					</CustomTable>
				</TableContainer>
				<BulkTablePagination
					limit={limit}
					page={page}
					totalElements={versioningCandidates.totalElements}
					onChangeLimit={handleChangeLimitPerPage}
					onChangePage={handleChangePage}
				/>
				<HorizontalDivider />
				<CustomDialog
					iconColor="var(--red-500)"
					iconModel={icon.faExclamationTriangle}
					open={!!warningMessage}
					subTitle={translate(warningMessage)}
					onClose={() => setWarningMessage("")}
					onSubmit={handleConfirm}
				/>
			</CustomDialog>
		</>
	);
};
export default BulkVersioningDialog;
